import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface UserJourney {
  id: string;
  user_id: string;
  current_stage: string;
  signup_date: string;
  first_facescan_at: string | null;
  last_facescan_at: string | null;
  last_routine_at: string | null;
  total_routines_completed: number;
  current_streak_days: number;
  longest_streak_days: number;
  last_active_at: string;
  created_at: string;
  updated_at: string;
}

export const useUserJourney = () => {
  return useQuery({
    queryKey: ['user-journey'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_journey')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      // If no journey exists, create one
      if (!data) {
        const { data: newJourney, error: insertError } = await supabase
          .from('user_journey')
          .insert([{
            user_id: user.id,
            current_stage: 'onboarding',
            signup_date: new Date().toISOString(),
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        return newJourney as UserJourney;
      }

      return data as UserJourney;
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateUserJourney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<UserJourney>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_journey')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-journey'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update journey');
    },
  });
};
